<script>
    import { onMount } from 'svelte';
    import { getApiBase } from './apiBase.js';
    
    // Form state
    let userQuery = '';
    let amount = '';
    let transactionType = 'payment';
    let description = 'Banko Pavedimas';
    let customDate = '';
    let useCustomDate = false;
    let isSubmitting = false;
    let successMessage = '';
    let errorMessage = '';

    // Past manual payments
    let manualPayments = [];
    let isLoadingManualPayments = false;
    let manualPaymentsError = '';
    let isDownloadingCsv = false;
    
    // User search and validation
    let students = [];
    let filteredStudents = [];
    let selectedUser = null;
    let showUserDropdown = false;
    let searchTimeout = null;
    
    // Load students on mount (similar to StudentInfoComponent)
    onMount(async () => {
        await loadStudents();
        await loadManualPayments();
    });

    const apiBase = getApiBase();
    
    // Load students list (adapted from StudentInfoComponent)
    async function loadStudents() {
        try {
            const token = localStorage.getItem('jwtToken');
            const response = await fetch(`${apiBase}/studentplayground/api/students-list/`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.status === 'success') {
                    students = data.students || [];
                    filteredStudents = students;
                } else {
                    throw new Error(data.message || 'Failed to load students');
                }
            } else {
                if (response.status === 401) {
                    alert('Reikia prisijungti iš naujo.');
                    localStorage.removeItem('jwtToken');
                    window.location.href = '#/about';
                } else {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            }
        } catch (err) {
            console.error('Error loading students:', err);
            errorMessage = `Error loading students: ${err.message}`;
        }
    }
    
    // Filter students (adapted from StudentInfoComponent)
    function filterStudents() {
        if (!userQuery.trim()) {
            filteredStudents = students;
            showUserDropdown = false;
        } else {
            const query = userQuery.toLowerCase();
            filteredStudents = students.filter(student => 
                student.name.toLowerCase().includes(query) ||
                student.email.toLowerCase().includes(query)
            );
            showUserDropdown = !selectedUser && filteredStudents.length > 0;
        }
    }
    
    // Select a user from search results (adapted from StudentInfoComponent)
    function selectUser(student) {
        selectedUser = student;
        userQuery = student.name;
        showUserDropdown = false;
    }
    
    // Reactive statement to filter students when search query changes
    $: if (userQuery !== undefined) {
        filterStudents();
    }
    
    // Validate form
    function validateForm() {
        if (!selectedUser) {
            return 'Please select a user';
        }
        
        if (!amount || isNaN(amount) || Number(amount) <= 0) {
            return 'Amount must be positive';
        }
        
        if (!description.trim()) {
            return 'Description is required';
        }
        
        if (useCustomDate && !customDate) {
            return 'Please select a custom date or uncheck the custom date option';
        }
        
        if (useCustomDate && customDate) {
            const selectedDate = new Date(customDate);
            const today = new Date();
            today.setHours(23, 59, 59, 999); // End of today
            
            if (selectedDate > today) {
                return 'Custom date cannot be in the future';
            }
        }
        
        return null;
    }
    
    // Submit manual payment
    async function submitManualPayment() {
        const validationError = validateForm();
        if (validationError) {
            errorMessage = validationError;
            return;
        }
        
        isSubmitting = true;
        errorMessage = '';
        successMessage = '';
        
        const token = localStorage.getItem('jwtToken');
        
        try {
            const requestData = {
                user_email: selectedUser.email,
                amount: Number(amount),
                transaction_type: transactionType,
                description: description.trim()
            };
            
            // Add custom creation date if specified
            if (useCustomDate && customDate) {
                requestData.custom_creation_date = customDate;
            }
            
            const response = await fetch(`${apiBase}/studentplayground/api/admin/create-manual-payment/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                // Enhanced success message with balance recalculation info
                const transactionLabel = transactionType === 'refund' ? 'Refund' : 'Payment';
                let message = `✅ ${transactionLabel} of €${amount} successfully added for ${selectedUser.name} (${selectedUser.email})`;
                
                // Add custom date info if used
                if (data.custom_date_used && data.creation_date) {
                    const creationDate = new Date(data.creation_date).toLocaleDateString();
                    message += ` with creation date: ${creationDate}`;
                }
                message += `.`;
                
                if (data.new_balance !== null) {
                    message += ` New balance: €${data.new_balance}`;
                    if (data.had_discrepancy) {
                        message += ` (Balance corrected)`;
                    }
                } else if (data.recalculation_error) {
                    message += ` ⚠️ Balance recalculation failed: ${data.recalculation_error}`;
                }
                
                successMessage = message;

                // Refresh list
                await loadManualPayments();
                
                // Reset form
                userQuery = '';
                amount = '';
                transactionType = 'payment';
                description = 'Banko Pavedimas';
                customDate = '';
                useCustomDate = false;
                selectedUser = null;
                showUserDropdown = false;
                filteredStudents = students;
                
                // Clear success message after 7 seconds (longer for more detailed messages)
                setTimeout(() => {
                    successMessage = '';
                }, 7000);
            } else {
                errorMessage = data.error || 'Failed to create manual payment';
            }
        } catch (error) {
            console.error('Error creating manual payment:', error);
            errorMessage = 'Network error. Please try again.';
        } finally {
            isSubmitting = false;
        }
    }

    async function loadManualPayments() {
        manualPaymentsError = '';
        manualPayments = [];

        isLoadingManualPayments = true;
        const token = localStorage.getItem('jwtToken');
        try {
            const url = `${apiBase}/studentplayground/api/admin/list-manual-payments/?limit=500`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    manualPayments = data.manual_payments || [];
                } else {
                    manualPaymentsError = data.error || 'Failed to load manual payments';
                }
            } else {
                if (response.status === 401) {
                    alert('Reikia prisijungti iš naujo.');
                    localStorage.removeItem('jwtToken');
                    window.location.href = '#/about';
                    return;
                }
                manualPaymentsError = `Failed to load manual payments (HTTP ${response.status})`;
            }
        } catch (e) {
            console.error('Error loading manual payments:', e);
            manualPaymentsError = 'Network error while loading manual payments';
        } finally {
            isLoadingManualPayments = false;
        }
    }

    async function downloadAllPaymentTransactionsCsv() {
        isDownloadingCsv = true;
        const token = localStorage.getItem('jwtToken');
        try {
            const url = `${apiBase}/studentplayground/api/admin/payment-transactions-csv/`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    alert('Reikia prisijungti iš naujo.');
                    localStorage.removeItem('jwtToken');
                    window.location.href = '#/about';
                    return;
                }
                const text = await response.text();
                throw new Error(text || `Download failed (HTTP ${response.status})`);
            }

            const blob = await response.blob();
            const href = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = href;
            a.download = 'payment_transactions.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(href);
        } catch (e) {
            console.error('CSV download failed:', e);
            alert('Nepavyko atsisiųsti CSV failo.');
        } finally {
            isDownloadingCsv = false;
        }
    }

    function formatDateTime(iso) {
        if (!iso) return '-';
        const d = new Date(iso);
        return d.toLocaleString('lt-LT', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    }
    
    // Clear messages when user starts typing
    function clearMessages() {
        if (errorMessage || successMessage) {
            errorMessage = '';
            successMessage = '';
        }
    }
</script>

<style>
    .manual-payment-container {
        max-width: 600px;
        margin: 0 auto;
        padding: 2rem;
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
    
    .header {
        text-align: center;
        margin-bottom: 2rem;
    }
    
    .header h2 {
        color: #14695b;
        font-size: 1.8rem;
        margin-bottom: 0.5rem;
    }
    
    .header p {
        color: #666;
        font-size: 1rem;
    }
    
    .form-group {
        margin-bottom: 1.5rem;
        position: relative;
    }
    
    .form-group label {
        display: block;
        font-weight: 600;
        color: #14695b;
        margin-bottom: 0.5rem;
        font-size: 1rem;
    }
    
    .required {
        color: #dc3545;
    }
    
    input {
        width: 100%;
        padding: 0.75rem;
        border: 2px solid #e9ecef;
        border-radius: 8px;
        font-size: 1rem;
        transition: border-color 0.3s ease;
        box-sizing: border-box;
    }
    
    input:focus {
        outline: none;
        border-color: #14695b;
    }
    
    .user-search-container {
        position: relative;
    }
    
    .search-input {
        width: 100%;
        padding: 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 0.9em;
        background-color: white;
    }

    .search-input:focus {
        outline: none;
        border-color: #14695b;
        box-shadow: 0 0 0 2px rgba(20, 105, 91, 0.2);
    }
    
    .search-results {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #ddd;
        border-top: none;
        border-radius: 0 0 6px 6px;
        max-height: 200px;
        overflow-y: auto;
        z-index: 1000;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    
    .search-result-item {
        padding: 0.75rem;
        cursor: pointer;
        border-bottom: 1px solid #f0f0f0;
        transition: background-color 0.2s ease;
    }
    
    .search-result-item:hover {
        background-color: #f8f9fa;
    }
    
    .search-result-item:last-child {
        border-bottom: none;
    }
    
    .user-info {
        font-weight: 500;
        color: #333;
        font-size: 0.9em;
    }
    
    .user-email {
        font-size: 0.8em;
        color: #666;
    }
    
    .amount-input {
        position: relative;
    }
    
    .euro-symbol {
        position: absolute;
        left: 0.75rem;
        top: 50%;
        transform: translateY(-50%);
        color: #666;
        font-weight: 600;
    }
    
    .amount-input input {
        padding-left: 2rem;
    }
    
    .submit-button {
        width: 100%;
        padding: 1rem 2rem;
        background-color: #14695b;
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 1.1rem;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.3s ease;
        margin-top: 1rem;
    }
    
    .submit-button:hover:not(:disabled) {
        background-color: #0f5a4f;
    }
    
    .submit-button:disabled {
        background-color: #6c757d;
        cursor: not-allowed;
    }
    
    .success-message {
        background-color: #d4edda;
        color: #155724;
        padding: 1rem;
        border-radius: 8px;
        margin-bottom: 1.5rem;
        border: 1px solid #c3e6cb;
    }
    
    .error-message {
        background-color: #f8d7da;
        color: #721c24;
        padding: 1rem;
        border-radius: 8px;
        margin-bottom: 1.5rem;
        border: 1px solid #f5c6cb;
    }
    
    .form-help {
        font-size: 0.9rem;
        color: #666;
        margin-top: 0.25rem;
    }
    
    .requirements-info {
        background-color: #e7f3ff;
        border: 1px solid #b8daff;
        border-radius: 8px;
        padding: 1rem;
        margin-bottom: 2rem;
    }
    
    .requirements-info h4 {
        color: #004085;
        margin-bottom: 0.5rem;
        font-size: 1rem;
    }
    
    .requirements-info ul {
        margin: 0;
        padding-left: 1.5rem;
        color: #004085;
    }
    
    .requirements-info li {
        margin-bottom: 0.25rem;
    }
    
    .selected-user-badge {
        display: inline-flex;
        align-items: center;
        background-color: #d1ecf1;
        color: #0c5460;
        padding: 0.5rem 0.75rem;
        border-radius: 6px;
        font-size: 0.9rem;
        margin-top: 0.5rem;
    }
    
    .selected-user-badge .remove-selection {
        background: none;
        border: none;
        color: #0c5460;
        margin-left: 0.5rem;
        cursor: pointer;
        font-weight: bold;
    }
    
    .checkbox-container {
        margin-bottom: 1rem;
    }
    
    .checkbox-label {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-weight: 600;
        color: #14695b;
        font-size: 1rem;
    }
    
    .checkbox-label input[type="checkbox"] {
        width: auto;
        margin-right: 0.5rem;
        transform: scale(1.2);
    }
    
    .date-input-container {
        margin-top: 1rem;
        padding: 1rem;
        background-color: #f8f9fa;
        border-radius: 8px;
        border: 1px solid #e9ecef;
    }
    
    .date-input-container label {
        display: block;
        font-weight: 600;
        color: #14695b;
        margin-bottom: 0.5rem;
        font-size: 1rem;
    }
    
    .date-input-container input[type="date"] {
        width: 100%;
        padding: 0.75rem;
        border: 2px solid #e9ecef;
        border-radius: 8px;
        font-size: 1rem;
        transition: border-color 0.3s ease;
        box-sizing: border-box;
    }
    
    .date-input-container input[type="date"]:focus {
        outline: none;
        border-color: #14695b;
    }

    .history {
        margin-top: 2rem;
        border-top: 1px solid #e9ecef;
        padding-top: 1.5rem;
    }

    .history h3 {
        margin: 0 0 0.75rem 0;
        color: #14695b;
        font-size: 1.1rem;
    }

    .history-list {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        max-height: 420px;
        overflow-y: auto;
        padding-right: 6px;
    }

    .history-row {
        border: 1px solid #eee;
        border-radius: 8px;
        padding: 0.75rem;
        background: #fafafa;
        display: flex;
        justify-content: space-between;
        gap: 1rem;
        align-items: flex-start;
    }

    .history-left {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        min-width: 0;
    }

    .history-meta {
        color: #333;
        font-weight: 600;
        font-size: 0.95rem;
    }

    .history-sub {
        color: #555;
        font-size: 0.9rem;
        word-break: break-word;
    }

    .history-email {
        color: #777;
        font-size: 0.85rem;
        word-break: break-word;
    }

    .history-parent {
        color: #555;
        font-size: 0.9rem;
        word-break: break-word;
    }

    .history-right {
        font-weight: 700;
        white-space: nowrap;
        color: #14695b;
    }

    .muted {
        color: #777;
        font-size: 0.9rem;
    }

    .csv-button {
        width: 100%;
        padding: 0.75rem 1rem;
        background-color: #0b6bcb;
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 0.95rem;
        font-weight: 700;
        cursor: pointer;
        transition: background-color 0.2s ease;
        margin-top: 1rem;
    }

    .csv-button:hover:not(:disabled) {
        background-color: #095aa9;
    }

    .csv-button:disabled {
        background-color: #6c757d;
        cursor: not-allowed;
    }
</style>

<div class="manual-payment-container">
    <div class="header">
        <h2>💰 Create Manual Payment</h2>
        <p>Add payment transactions for users who paid outside the system</p>
    </div>
    
    <div class="requirements-info">
        <h4>ℹ️ Transaction Requirements</h4>
        <ul>
            <li><strong>User:</strong> Select from searchable user list (required)</li>
            <li><strong>Amount:</strong> Positive EUR amount with up to two decimal places (required)</li>
            <li><strong>Transaction Type:</strong> Payment adds to the balance; refund deducts from it</li>
            <li><strong>Description:</strong> Default "Banko Pavedimas" (editable)</li>
            <li><strong>Creation Date:</strong> Uses current date/time by default, or set custom date</li>
            <li><strong>Related Objects:</strong> Not required for manual transactions</li>
        </ul>
    </div>
    
    {#if successMessage}
        <div class="success-message">
            {successMessage}
        </div>
    {/if}
    
    {#if errorMessage}
        <div class="error-message">
            {errorMessage}
        </div>
    {/if}
    
    <form on:submit|preventDefault={submitManualPayment}>
        <!-- User Search -->
        <div class="form-group">
            <label for="userQuery">
                Select User <span class="required">*</span>
            </label>
            <div class="user-search-container">
                <input
                    type="text"
                    id="userQuery"
                    bind:value={userQuery}
                    on:input={() => {
                        if (selectedUser && userQuery !== selectedUser.name) {
                            selectedUser = null;
                            showUserDropdown = true;
                        } else if (!selectedUser) {
                            showUserDropdown = userQuery.trim().length > 0;
                        }
                        clearMessages();
                    }}
                    placeholder="Search user by name or email..."
                    autocomplete="off"
                    class="search-input"
                    required
                />
                
                {#if selectedUser}
                    <div class="selected-user-badge">
                        ✓ {selectedUser.name} ({selectedUser.email})
                        <button 
                            type="button" 
                            class="remove-selection"
                            on:click={() => {
                                selectedUser = null;
                                userQuery = '';
                                showUserDropdown = false;
                                filteredStudents = students;
                            }}
                        >
                            ×
                        </button>
                    </div>
                {/if}
                
                {#if showUserDropdown}
                    <div class="search-results">
                        {#each filteredStudents.slice(0, 8) as student}
                            <div 
                                class="search-result-item"
                                on:click={() => selectUser(student)}
                                role="button"
                                tabindex="0"
                                on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectUser(student)}
                            >
                                <div class="user-info">{student.name}</div>
                                <div class="user-email">{student.email}</div>
                            </div>
                        {/each}
                    </div>
                {/if}
            </div>
            <div class="form-help">Start typing to search for users by name or email</div>
        </div>

        {#if transactionType === 'payment'}
            <div class="form-help">Payments cover the oldest unpaid classes first. Any remainder stays as account credit.</div>
        {/if}
        
        <!-- Amount -->
        <div class="form-group">
            <label for="amount">
                Amount <span class="required">*</span>
            </label>
            <div class="amount-input">
                <span class="euro-symbol">€</span>
                <input
                    type="number"
                    id="amount"
                    bind:value={amount}
                    on:input={clearMessages}
                    step="0.01"
                    min="0.01"
                    placeholder="0"
                    required
                />
            </div>
            <div class="form-help">Enter a positive amount; refunds are stored as a deduction automatically</div>
        </div>

        <div class="form-group">
            <label for="transactionType">Transaction Type <span class="required">*</span></label>
            <select id="transactionType" bind:value={transactionType} on:change={() => {
                description = transactionType === 'refund' ? 'Pinigų grąžinimas' : 'Banko Pavedimas';
                clearMessages();
            }}>
                <option value="payment">Payment</option>
                <option value="refund">Refund</option>
            </select>
        </div>
        
        <!-- Description -->
        <div class="form-group">
            <label for="description">
                Description <span class="required">*</span>
            </label>
            <input
                type="text"
                id="description"
                bind:value={description}
                on:input={clearMessages}
                placeholder="Banko Pavedimas"
                required
            />
            <div class="form-help">Description for the payment transaction</div>
        </div>
        
        <!-- Custom Creation Date -->
        <div class="form-group">
            <div class="checkbox-container">
                <label class="checkbox-label">
                    <input
                        type="checkbox"
                        bind:checked={useCustomDate}
                        on:change={clearMessages}
                    />
                    <span class="checkmark"></span>
                    Use Custom Creation Date
                </label>
            </div>
            
            {#if useCustomDate}
                <div class="date-input-container">
                    <label for="customDate">
                        Creation Date <span class="required">*</span>
                    </label>
                    <input
                        type="date"
                        id="customDate"
                        bind:value={customDate}
                        on:input={clearMessages}
                        max={new Date().toISOString().split('T')[0]}
                        required={useCustomDate}
                    />
                    <div class="form-help">The date when the payment was actually received (cannot be in the future)</div>
                </div>
            {/if}
            
            {#if !useCustomDate}
                <div class="form-help">Payment will be recorded with current date and time</div>
            {/if}
        </div>
        
        <button 
            type="submit" 
            class="submit-button" 
            disabled={isSubmitting}
        >
            {#if isSubmitting}
                Creating Payment Transaction...
            {:else}
                💰 Create Payment Transaction
            {/if}
        </button>
    </form>

    <div class="history">
        <h3>Past Manual Balance Transactions</h3>
        <button class="csv-button" on:click={downloadAllPaymentTransactionsCsv} disabled={isDownloadingCsv}>
            {#if isDownloadingCsv}
                Downloading CSV…
            {:else}
                Download ALL payment transactions CSV
            {/if}
        </button>

        {#if isLoadingManualPayments}
            <div class="muted">Loading…</div>
        {:else if manualPaymentsError}
            <div class="error-message">{manualPaymentsError}</div>
        {:else if manualPayments.length === 0}
            <div class="muted">No manual payments found.</div>
        {:else}
            <div class="history-list">
                {#each manualPayments as mp (mp.id)}
                    <div class="history-row">
                        <div class="history-left">
                            <div class="history-meta">
                                {mp.user_first_name} {mp.user_last_name}
                            </div>
                            <div class="history-email">{mp.user_email}</div>
                            {#if mp.parent_name}
                                <div class="history-parent"><strong>Parent:</strong> {mp.parent_name}</div>
                            {:else}
                                <div class="history-parent"><strong>Parent:</strong> -</div>
                            {/if}
                            <div class="history-sub">
                                <strong>Created:</strong> {formatDateTime(mp.created_at)}
                            </div>
                            <div class="history-sub">
                                <strong>Description:</strong> {mp.description}
                            </div>
                        </div>
                        <div class="history-right">
                            <div>{mp.transaction_type === 'refund' ? 'Refund' : 'Payment'}</div>
                            <div>€{mp.amount}</div>
                        </div>
                    </div>
                {/each}
            </div>
        {/if}
    </div>
</div>
